import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Constants } from 'src/app/app.constants';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  MerchantService,
  AuthService,
  ExcelService,
} from '../../../services/index';
import { Customer } from '../../../models/index';
import { MatDialog } from '@angular/material/dialog';
import { GiveRewardDialogComponent } from '../give-reward-dialog/give-reward-dialog.component';
import { GiveStampsDialogComponent } from '../give-stamps-dialog/give-stamps-dialog.component';
import { PendingRewardsDialogComponent } from '../pending-rewards-dialog/pending-rewards-dialog.component';
import { AdjustPointsDialogComponent } from '../adjust-points-dialog/adjust-points-dialog.component';
import { CreateCustomerDialogComponent } from '../create-customer-dialog/create-customer-dialog.component';
import { NoopScrollStrategy } from '@angular/cdk/overlay';

@Component({
  selector: 'customers',
  templateUrl: './customers.component.html',
  styleUrls: ['./customers.component.css'],
})
export class CustomersComponent {
  @Input() smallTitle = false;
  @Input() title = '';
  @Input() subtitle: string = '';
  @ViewChild('myTable') table: any;

  pageIndex = 0;
  pageSize = 30;
  length = 0;
  customers: Customer[] = [];
  loading = false;
  error = false;
  exporting = false;
  searching = false;
  scrollTimeout: any;
  suppressPaging: boolean = false;
  customersRange = '';
  searchTerm = '';

  sortKey = '_id';
  sortDir = 'desc';

  isHandset$: Observable<boolean> = this.breakpointObserver
    .observe(['(max-width: 950px)'])
    .pipe(map((result) => result.matches));

  constructor(
    public dialog: MatDialog,
    public constants: Constants,
    private authService: AuthService,
    private merchantService: MerchantService,
    private router: Router,
    private excelService: ExcelService,
    private breakpointObserver: BreakpointObserver,
  ) {}

  ngOnInit() {
    this.subtitle = this.authService.getNestedUserProperty('merchant', 'name');

    this.loading = true;
    this.getCustomers();
  }

  get emptyString(): string {
    return `No ${this.constants.strings.customers} to display`;
  }

  getCustomers(): void {
    this.merchantService
      .getCustomers(
        this.sortKey,
        this.sortDir,
        this.pageSize,
        this.pageIndex,
        this.searchTerm,
        {},
      )
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.length = res.totalDocs;
            this.customers = res.docs;

            this.customersRange = this.constants.pageCounter(
              this.pageIndex,
              this.pageSize,
              this.length,
              this.customers?.length,
            );
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => ((this.loading = false), (this.searching = false)));
  }

  pageEvent(event: any) {
    if (!this.suppressPaging) {
      this.pageIndex = event.offset;
      this.searching = true;
      this.getCustomers();
    }
  }

  rowTappedNGX({ row, type }: any) {
    if (type === 'click') {
      this.router.navigate([Constants.routes.customer + '/' + row.nanoid]);
    }
  }
  giveReward(): void {
    this.dialog.open(GiveRewardDialogComponent, {
      scrollStrategy: new NoopScrollStrategy(),
      width: '450px',
      autoFocus: false,
      panelClass: 'custom-dialog-panel',
      disableClose: true,
    });
  }

  giveStamps(): void {
    const dialogRef = this.dialog.open(GiveStampsDialogComponent, {
      scrollStrategy: new NoopScrollStrategy(),
      width: '600px',
      autoFocus: false,
      panelClass: 'custom-dialog-panel',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.success) {
        window.location.reload();
      }
    });
  }

  openPendingRewards(): void {
    this.dialog.open(PendingRewardsDialogComponent, {
      scrollStrategy: new NoopScrollStrategy(),
      width: '1000px',
      maxWidth: '90vw',
      autoFocus: false,
      panelClass: 'custom-dialog-panel',
      disableClose: true,
    });
  }

  openAdjustPoints(): void {
    const dialogRef = this.dialog.open(AdjustPointsDialogComponent, {
      data: {
        // No customer provided - dialog will handle customer selection
      },
      scrollStrategy: new NoopScrollStrategy(),
      width: '500px',
      autoFocus: false,
      panelClass: 'custom-dialog-panel',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.success) {
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    });
  }

  createCustomer(): void {
    const dialogRef = this.dialog.open(CreateCustomerDialogComponent, {
      scrollStrategy: new NoopScrollStrategy(),
      width: '500px',
      autoFocus: false,
      panelClass: 'custom-dialog-panel',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.getCustomers();
      }
    });
  }

  handleScroll() {
    this.suppressPaging = true;

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    this.scrollTimeout = setTimeout(() => {
      this.suppressPaging = false;
    }, 100);
  }

  export(): void {
    this.exporting = true;
    this.merchantService
      .export('customers')
      .subscribe({
        next: (res: any) => {
          if (res.data && res.data.length === 0) {
            this.constants.snack('No customers to export');
          } else {
            this.excelService.exportExcelFile(res.data.data, res.fileName);
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.exporting = false));
  }

  searchCustomers(): void {
    this.pageIndex = 0;
    this.searching = true;
    this.getCustomers();
  }

  cancel(): void {
    this.pageIndex = 0;
    this.searchTerm = '';
    this.searching = true;
    this.getCustomers();
  }

  onSort(event: any): void {
    if (event.sorts && event.sorts.length > 0) {
      const sort = event.sorts[0];
      this.sortKey = sort.prop;
      this.sortDir = sort.dir;
      this.searching = true;
      this.getCustomers();
    }
  }
}
